import React, { useContext, useEffect, useRef, useState } from "react";
import BasketContext from "../services/BasketContext";
import ExpressCheckoutService from "../services/ExpressCheckoutService";
import useNotification from "./notification/useNotification";
import NotificationType from "./notification/NotificationType";
import GCPaymentProvider from "../../../../Site.Checkout/src/src/paymentDetailsStep/GCPaymentProviders.enum";
import Logger from "../../../gulp/scripts/typescript/modules/logger";
import { ISaveDeliveryAddressResponse } from "../../../../Site.Checkout/src/src/models/ISaveDeliveryAddressResponse.model";
import Step from "../../../../Site.Checkout/src/src/common/Step.enum";
import Loader from "./Loader";
import PaymentOverlay from "./PaymentOverlay";
import { isChannelIslandsPostcode } from "../helpers/expressCheckoutAddress";

declare var dataLayer: any;

function ExpressPayPalButtons(props: { fundingSource: "paypal" | "paylater" }) {

	const paymentType = props.fundingSource === "paypal" ? "paypal" : "paypal_pay_in_3";

	const basketContext = useContext(BasketContext);
	const expressCheckoutService = new ExpressCheckoutService();
	const { addNotification } = useNotification();
	const buttonContainerRef = useRef<HTMLDivElement>(null);
	const paypalButtonInstanceRef = useRef<any>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [guestEmail, setGuestEmail] = useState<string | null>(null);
	const [isButtonReady, setIsButtonReady] = useState(false);

	const basketContextRef = useRef(basketContext);
	useEffect(() => {
		basketContextRef.current = basketContext;
	}, [basketContext]);

	useEffect(() => {
		// The PayPal SDK <script> is `defer`red, so it may not be ready at mount - poll briefly
		// instead of checking once, otherwise a slow SDK load leaves the button unrendered forever.
		let pollIntervalId: number | undefined;

		function tryRender() {
			if ((window as any).paypal && buttonContainerRef.current) {
				if (pollIntervalId !== undefined) {
					window.clearInterval(pollIntervalId);
				}
				renderButton();
			}
		}

		const paypalWasAvailable = !!(window as any).paypal;
		tryRender();
		if (!paypalWasAvailable) {
			pollIntervalId = window.setInterval(tryRender, 100);
		}

		return () => {
			if (pollIntervalId !== undefined) {
				window.clearInterval(pollIntervalId);
			}
			if (paypalButtonInstanceRef.current) {
				try {
					if (typeof paypalButtonInstanceRef.current.close === "function") {
						paypalButtonInstanceRef.current.close();
					}
				} catch (error) {
					// Silently handle any errors during cleanup, matching PayPalButton.tsx precedent
				}
				paypalButtonInstanceRef.current = null;
			}
		};
	}, []);

	function renderButton() {
		if (!buttonContainerRef.current || !(window as any).paypal) {
			return;
		}

		buttonContainerRef.current.innerHTML = "";

		const paypalButton = (window as any).paypal.Buttons({
			style: {
				shape: "sharp",
				color: props.fundingSource === "paypal" ? "gold" : "blue",
				layout: "horizontal",
				label: "paypal",
				tagline: false,
				height: 46,
				disableMaxWidth: true,
			},
			fundingSource: props.fundingSource,
			createOrder: createOrder,
			onShippingAddressChange: onShippingAddressChange,
			onShippingOptionsChange: onShippingOptionsChange,
			onApprove: onApprove,
			onError: onError,
			onCancel: onCancel
		});

		paypalButton.render(buttonContainerRef.current)
			.then(() => {
				paypalButtonInstanceRef.current = paypalButton;
				setIsButtonReady(true);
			})
			.catch((error: Error) => {
				if (error?.message?.includes("Detected container element removed from DOM")) {
					// fail silently, matching PayPalButton.tsx precedent
				} else if (error?.message?.toLowerCase().includes("paylater is not eligible")) {
					Logger.trackException(error, "Express PayPal paylater button not eligible");
				} else {
					Logger.trackException(error, "Express PayPal button render failed");
					onError(error);
				}
			});
	}

	async function ensureSession() {
		// init() has no dependency on an email/guest session and can run any time.
		return expressCheckoutService.init();
	}

	function getGA4Items() {
		return basketContextRef.current?.basket.LineItems.map((lineItem) => ({
			...lineItem.ViewItemEventInfo,
			quantity: lineItem.Quantity
		}));
	}

	function trackBeginCheckout() {
		dataLayer = dataLayer || [];
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: "begin_checkout",
			ecommerce: {
				value: basketContextRef.current?.basket.Total.Price,
				items: getGA4Items()
			}
		});
	}

	function trackAddPaymentInfo() {
		dataLayer = dataLayer || [];
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: "add_payment_info",
			ecommerce: {
				payment_type: paymentType,
				value: basketContextRef.current?.basket.Total.Price,
				items: getGA4Items()
			}
		});
	}

	function createOrder(data, actions) {
		Logger.trackTrace("ExpressPayPalJsSdkPayment - createOrder called.");
		trackBeginCheckout();

		return ensureSession()
			.then((initResponse) => {
				// A prior express checkout attempt on this basket may have already negotiated real
				// delivery options (e.g. Google Pay cancelled, then PayPal clicked) - furthestValidStep
				// mirrors the backend's HasValidDeliveryOptions() check, which then expects the full total.
				const hasValidDeliveryOptions = initResponse?._embedded?.summary?.furthestValidStep === Step.PaymentDetails;
				const paymentAmount = hasValidDeliveryOptions
					? initResponse._embedded.summary.total.amount
					: basketContextRef.current?.basket.SubTotal.Price;

				return expressCheckoutService.initialisePayment({
					selectedPaymentMethodName: GCPaymentProvider.PayPalJsSdk,
					// PayPal only reveals the payer's email once the order is approved (see onApprove) -
					// not yet known here.
					email: "",
					// Reads from the ref, not the closed-over basketContext, since createOrder is
					// captured once at mount by the imperative PayPal SDK render.
					paymentAmount
				});
			})
			.then((response) => {
				if (!response.success || !response.orderRequestWithAddress) {
					throw new Error(response.errorMessages?.join("\n") || "Failed to initialise PayPal order.");
				}
				const orderRequest = JSON.parse(response.orderRequestWithAddress);
				return actions.order.create(orderRequest);
			})
			.catch((error) => {
				onError(error, "ExpressPayPalJsSdkPayment - unexpected error in createOrder.");
			});
	}

	function onShippingAddressChange(data) {
		// TEMP: only allow GB addresses while we fix other issues
		if (data.shippingAddress?.countryCode !== "GB" || isChannelIslandsPostcode(data.shippingAddress?.postalCode)) {
			Logger.trackTrace(`ExpressPayPalJsSdkPayment - rejected non-GB shipping address (${data.shippingAddress?.countryCode} ${data.shippingAddress?.postalCode}).`);
			return Promise.reject(new Error("Please use the full checkout for delivery to addresses outside of mainland UK. Please click Go to Checkout to continue."));
		}

		return expressCheckoutService.saveCoarseAddress({
			city: data.shippingAddress?.city,
			postcode: data.shippingAddress?.postalCode,
			countryCode: data.shippingAddress?.countryCode,
			payPalOrderId: data.orderID
		})
			.then((response) => {
				const deliveryOptions = (response as ISaveDeliveryAddressResponse)?._embedded?.deliveryOptions;
				if (!deliveryOptions?.deliveryOptions?.length) {
					throw new Error("No delivery options available for this address.");
				}
			});
	}

	function onShippingOptionsChange(data) {
		return expressCheckoutService.saveDeliveryOptions({
			selectedDeliveryOption: data.selectedShippingOption?.id,
			payPalOrderId: data.orderID
		});
	}

	function onApprove(data, actions) {
		Logger.trackTrace("ExpressPayPalJsSdkPayment - onApprove called.");
		setIsSaving(true);

		// PayPal only reveals the payer's email once the order is approved - completeOrder's email
		// field sets up the guest session (or attaches to an existing account by email match)
		// server-side, unless already logged in. See ExpressGuestAuthenticationService.
		return actions.order.get()
			.then((orderDetails) => {
				// TEMP: only allow GB addresses while we fix other issues - re-checked here as a final check
				const shippingAddress = orderDetails?.purchase_units?.[0]?.shipping?.address;
				if (shippingAddress?.country_code !== "GB" || isChannelIslandsPostcode(shippingAddress?.postal_code)) {
					Logger.trackTrace(`ExpressPayPalJsSdkPayment - rejected non-GB shipping address at approve (${shippingAddress?.country_code} ${shippingAddress?.postal_code}).`);
					throw new Error("Please use the full checkout for delivery to addresses outside of mainland UK. Please click Go to Checkout to continue.");
				}

				const email = orderDetails?.payer?.email_address ?? "";
				setGuestEmail(email);
				trackAddPaymentInfo();
				return email;
			})
			.then((email) => expressCheckoutService.completeOrder({
				email,
				selectedPaymentMethodName: GCPaymentProvider.PayPalJsSdk,
				payPalOrderId: data.orderID,
				payPalPayerId: data.payerID
			}).catch(labelError("completeOrder")))
			.then((completeOrderResponse) => {
				if (completeOrderResponse.success) {
					Logger.trackTrace(`ExpressPayPalJsSdkPayment - complete order successful ${completeOrderResponse.successRedirectUrl}.`);
					window.location.replace(completeOrderResponse.successRedirectUrl);
				} else {
					onError(new Error(`ExpressPayPalJsSdkPayment - complete order failed: ${completeOrderResponse.failureCustomerMessage}.`));
				}
			})
			.catch((error) => {
				onError(error, "ExpressPayPalJsSdkPayment - unexpected error in complete order.");
			});
	}

	// Labels which specific await failed once it reaches the outer catch in onApprove -
	// completeOrder() would otherwise report the same "unexpected error in complete order" as any
	// other failure in this chain, with no way to tell it apart.
	function labelError(label: string) {
		return (error: any) => {
			throw new Error(`[${label}] ${error?.message || String(error)}`, {
				cause: error,
			});
		};
	}

	function onError(error: Error, additionalExceptionErrorMessage?: string) {
		setIsSaving(false);
		Logger.trackException(error, additionalExceptionErrorMessage);

		addNotification({
			headline: "There was a problem with PayPal, please try again or use an alternative payment method.",
			type: NotificationType.error,
			autoDismissMilliseconds: 0
		});
	}

	function onCancel() {
		setIsSaving(false);
		Logger.trackTrace("ExpressPayPalJsSdkPayment - user cancelled PayPal payment.");
	}

	return (
		<div className="c-basket__express-paypal-button">
			{/* Separate element, not nested inside the container below - renderButton() wipes that
			container's innerHTML directly, which would silently remove a React-rendered child. */}
			{!isButtonReady && <Loader />}
			<div
				ref={buttonContainerRef}
				style={{ display: isButtonReady ? "block" : "none" }}
				data-test={`ExpressPayPalJsSdkPayment-${props.fundingSource}`}
			></div>
			{isSaving ? <PaymentOverlay /> : null}
		</div>
	);
}

export default ExpressPayPalButtons;
